import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Tabs,
  Tab,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import BotStatsDetailedView from './IndividualBotView/_BoxStatsDetailedView';
import { BotGroupContainer } from './SummaryView/_BotGroupContainer';
import { IBuyBack, IBotData, IManualMovement } from '../shared/types';
import BuybacksView from './BuyBacksView';
import TimelineView from './TimelineView';
import { useResponsive } from '../hooks/useResponsive';
import { useSocket } from '../components/SocketProvider';
import { ReconnectingWebSocket } from '../socket';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';
import { isWaitingForFunds } from '../utils/botUtils';
import { BOT_STATE_LABEL, BOT_STATE_TONE, botState, gasSeverity } from '../utils/botMetrics';
import { StatusDot, StatusTone } from '../components/ui/StatusPill';
import { tokens } from '../theme';
import { botShortLabel } from '../utils/botNames';

// A simple component to display tab content
function TabPanel(props: {
  children?: React.ReactNode;
  index: string;
  value: string;
}) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const STATIC_TABS: Record<string, string> = {
  SUMMARY: "Summary",
  BUYBACKS: "Buybacks",
  TIMELINE: "Timeline",
};

interface BotStatsContainerProps {
  bots: IBotData[];
  buybacksData: {
    completed: IBuyBack[];
    pending: IBuyBack[];
  };
  pendingBuybacksSummary?: Record<string, { remainingBudget: number, totalSpent: number, count: number, avgPrice: number, baseAsset: string, quoteAsset: string }>;
  onEditMovement: (movement: IManualMovement) => void;
  onDeleteMovement: (id: string) => Promise<void>;
  forceHomeTab?: boolean;
  onDoubleClickBot?: (botId: string) => void;
  onDoubleClickBack?: () => void;
  activeNetwork: string;
  isAdmin: boolean;
  socket: ReconnectingWebSocket;
  /** Offset of the sticky app bar the tab strip docks under (px or CSS length). */
  stickyTop?: number | string;
  onFeedback?: (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;
}

const BotStatsContainer: React.FC<BotStatsContainerProps> = ({
  bots,
  buybacksData,
  pendingBuybacksSummary,
  onEditMovement,
  onDeleteMovement,
  forceHomeTab = false,
  onDoubleClickBot,
  onDoubleClickBack,
  activeNetwork,
  stickyTop = 64,
  onFeedback,
}) => {
  // Active tab is part of the URL (?t=…): replace on tab switches, push when a bot card opens a bot.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("t") ?? "SUMMARY";
  const setTab = (value: string, push = false) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "SUMMARY") next.delete("t");
        else next.set("t", value);
        return next;
      },
      { replace: !push }
    );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const { isMobile } = useResponsive();
  const { socket } = useSocket();
  const clientId = useRef(uuidv4()); // Generate a unique client ID once

  // The tab pill docks under the top bar; once docked, a frosted band keeps scrolled content from showing
  // around it, and an edge under the band fades content out. `--band-edge` (0 → 1 over the first 24px)
  // is how much tab content has actually gone under the band: the edge appears with it, so content resting
  // just below the docked pill (right after a tab switch) stays crisp. Set on the element, not in state:
  // no re-render per scroll frame.
  const stripRef = useRef<HTMLDivElement>(null);
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const top = parseFloat(getComputedStyle(el).top) || 0;
      const rect = el.getBoundingClientRect();
      setDocked(window.scrollY > 0 && rect.top <= top + 0.5);
      const content = el.nextElementSibling;
      if (content) {
        const edgeTop = rect.top + (parseFloat(getComputedStyle(el, "::after").top) || rect.height);
        const under = edgeTop - content.getBoundingClientRect().top;
        el.style.setProperty("--band-edge", Math.min(1, Math.max(0, under / 24)).toFixed(3));
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Phones: the strip slides when the top bar hides or shows (its `top` transition).
    el.addEventListener('transitionend', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      el.removeEventListener('transitionend', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Effect to register client ID with WebSocket backend
  useEffect(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send("registerClient", { clientId: clientId.current });
    }
  }, [socket]);

  // --- Swipe between tabs (phones) ---
  // Only clearly horizontal swipes count, and never when the gesture starts on
  // a chart, a table or the tab strip (reading a chart must not switch bots).
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const SWIPE_MIN_X = 80;

  const handleTouchStart = (event: React.TouchEvent) => {
    if (!isMobile) return;
    const target = event.target as HTMLElement;
    if (target.closest(".recharts-wrapper, table, [role=tablist], .MuiSlider-root, input, textarea")) {
      touchStart.current = null;
      return;
    }
    touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!isMobile || !start) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dx) < 2 * Math.abs(dy)) return;

    const currentIndex = tabOptions.indexOf(safeTab);
    if (dx > 0 && currentIndex > 0) {
      selectTab(tabOptions[currentIndex - 1]);
    } else if (dx < 0 && currentIndex < tabOptions.length - 1) {
      selectTab(tabOptions[currentIndex + 1]);
    }
  };

  const tabOptions = useMemo(() => {
    if (forceHomeTab) {
      return ["SUMMARY", "BUYBACKS", "TIMELINE"];
    } else {
      const botNames = bots
        .map((b) => b.stats.botName)
        .sort((a, b) => a.localeCompare(b));
      return ["SUMMARY", ...botNames];
    }
  }, [bots, forceHomeTab]);

  const safeTab = tabOptions.includes(tab) ? tab : "SUMMARY";

  const currentBot = useMemo(() => {
    if (safeTab === "SUMMARY" || safeTab === "BUYBACKS" || safeTab === "TIMELINE") return null;
    return bots.find((b) => b.stats.botName === safeTab) || null;
  }, [safeTab, bots]);

  // New tab content starts right under the docked strip instead of mid-page.
  const selectTab = (value: string, push = false) => {
    setTab(value, push);
    const el = stripRef.current;
    if (!el) return;
    const stickyOffset = parseFloat(getComputedStyle(el).top) || 0;
    const container = el.parentElement;
    if (!container) return;
    const naturalTop = container.getBoundingClientRect().top + window.scrollY - stickyOffset;
    if (window.scrollY > naturalTop) {
      // Tell the shell this scroll is ours, so the phone top bar doesn't pop back in and cover the strip.
      window.dispatchEvent(new CustomEvent("app:programmatic-scroll"));
      window.scrollTo({ top: naturalTop, behavior: "auto" });
    }
  };
  const handleTabChange = (_: any, newValue: string) => selectTab(newValue);
  const handleSnackbarClose = () => setSnackbarOpen(false);
  // The family is already in the page title, so bot tabs read "Bot 1", "Bot 2"… on every screen
  // (botShortLabel; the full id is in the tab's tooltip, and right-click copies it).
  const fillWidth = isMobile && tabOptions.length <= 4;

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Saved as the detail view settled it: amountUSD is no longer overwritten with quantity × price here
  // (that discarded an edited amount, and wrote $0 for movements without quantity or price).
  const handleEditMovement = (movement: IManualMovement) => {
    onEditMovement(movement);
    if (onFeedback) {
      onFeedback("Movement updated", 'success');
    } else {
      showSnackbar("Movement updated");
    }
  };

  const handleDeleteMovement = async (id: string) => {
    try {
      await onDeleteMovement(id);
      if (onFeedback) {
        onFeedback("Movement deleted", 'success');
      } else {
        showSnackbar("Movement deleted");
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to delete movement';
      if (onFeedback) {
        onFeedback(msg, 'error');
      } else {
        showSnackbar(msg);
      }
    }
  };

  const handleTabContextMenu = (
    event: React.MouseEvent<HTMLDivElement>,
    botId: string
  ) => {
    if (STATIC_TABS[botId]) return;
    event.preventDefault();
    navigator.clipboard.writeText(botId);
    showSnackbar(`Bot ID "${botId}" copied to clipboard`);
  };

  const botStatus = (
    botId: string
  ): { tone: StatusTone; label: string; lowGas: boolean; gasTone: "error" | "warning"; waiting: boolean } | null => {
    const bot = bots.find(b => b.stats.botName === botId);
    if (!bot) return null;
    const state = botState(bot.stats);
    return {
      tone: BOT_STATE_TONE[state],
      label: BOT_STATE_LABEL[state],
      lowGas: bot.stats.isLowGas === true,
      gasTone: gasSeverity(bot.stats),
      waiting: isWaitingForFunds(bot.stats),
    };
  };

  return (
    <Box>
      {/* Sticky glass tab pill */}
      <Box
        ref={stripRef}
        sx={{
          position: "sticky",
          top: stickyTop,
          transition: "top 220ms ease",
          zIndex: (theme) => theme.zIndex.appBar - 1,
          mb: { xs: 2, md: 3 },
          display: "flex",
          pointerEvents: "none",
          // Docked: a frosted band (same material as the mobile top bar) behind the pill.
          "&::before": {
            content: '""',
            position: "absolute",
            left: { xs: -16, sm: -24, md: -12 },
            right: { xs: -16, sm: -24, md: -12 },
            // Phones: reach up under the status bar too (the top bar may be hidden).
            top: { xs: "calc(-8px - env(safe-area-inset-top))", md: -14 },
            bottom: { xs: -8, md: -10 },
            zIndex: -1,
            pointerEvents: "none",
            bgcolor: "rgba(0,0,0,0.62)",
            backdropFilter: "blur(20px) saturate(160%)",
            WebkitBackdropFilter: "blur(20px) saturate(160%)",
            opacity: docked ? 1 : 0,
            transition: "opacity 180ms ease",
          },
          // Soft edge under the band instead of a hard line (no double border with cards). Fully opaque where it
          // meets the band, then fading out: the band blurs a card border away, so a translucent start let the
          // border of a card scrolling under it peek out below the band as a hairline. Scroll-linked through
          // --band-edge (no transition: it follows the content).
          "&::after": {
            content: '""',
            position: "absolute",
            left: { xs: -16, sm: -24, md: -12 },
            right: { xs: -16, sm: -24, md: -12 },
            top: { xs: "calc(100% + 8px)", md: "calc(100% + 10px)" },
            height: 32,
            zIndex: -1,
            pointerEvents: "none",
            background: "linear-gradient(180deg, #000 0%, #000 60%, rgba(0,0,0,0) 100%)",
            opacity: docked ? "var(--band-edge, 1)" : 0,
          },
        }}
      >
        <Box
          sx={{
            pointerEvents: "auto",
            minWidth: 0,
            maxWidth: "100%",
            width: { xs: "100%", sm: "auto" },
            p: "5px",
            borderRadius: 999,
            bgcolor: "rgba(18,18,20,0.72)",
            backdropFilter: "blur(22px) saturate(160%)",
            WebkitBackdropFilter: "blur(22px) saturate(160%)",
            border: `1px solid ${tokens.glass.border}`,
            boxShadow: `${tokens.glass.highlight}, 0 18px 40px -18px rgba(0,0,0,0.95)`,
          }}
        >
          <Tabs
            value={safeTab}
            onChange={handleTabChange}
            variant={fillWidth ? "fullWidth" : "scrollable"}
            scrollButtons={false}
            textColor="inherit"
            aria-label="Views"
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTabs-scroller": {
                borderRadius: 999,
                maskImage: isMobile && !fillWidth ? "linear-gradient(90deg, #000 88%, transparent)" : "none",
              },
            }}
          >
            {tabOptions.map((option) => {
              const status = STATIC_TABS[option] ? null : botStatus(option);
              return (
                <Tab
                  key={option}
                  value={option}
                  disableRipple
                  title={STATIC_TABS[option] ? undefined : option}
                  onContextMenu={(e) => handleTabContextMenu(e, option)}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {status && (
                        <Tooltip title={status.label}>
                          <Box component="span" sx={{ display: "inline-flex" }}>
                            <StatusDot tone={status.tone} size={7} />
                          </Box>
                        </Tooltip>
                      )}
                      <span>{STATIC_TABS[option] ?? botShortLabel(option)}</span>
                      {status?.lowGas && (
                        <Tooltip title="Low gas balance">
                          <LocalGasStationRoundedIcon sx={{ fontSize: 14, color: `${status.gasTone}.main` }} />
                        </Tooltip>
                      )}
                      {status?.waiting && !fillWidth && (
                        <Tooltip title="Waiting for external funds">
                          <HourglassBottomRoundedIcon sx={{ fontSize: 15, color: "warning.main" }} />
                        </Tooltip>
                      )}
                    </Stack>
                  }
                  sx={{
                    minWidth: fillWidth ? 0 : undefined,
                    whiteSpace: "nowrap",
                    minHeight: 36,
                    height: 36,
                    "@media (pointer: coarse)": { minHeight: 44, height: 44 },
                    px: fillWidth ? 1 : { xs: 1.5, md: 2 },
                    borderRadius: 999,
                    fontSize: "0.8125rem",
                    fontWeight: 650,
                    color: "text.secondary",
                    transition: "background-color 140ms ease, color 140ms ease, box-shadow 140ms ease",
                    "&:hover": { color: "text.primary", bgcolor: "rgba(255,255,255,0.05)" },
                    "&.Mui-selected": {
                      color: "text.primary",
                      bgcolor: "rgba(255,255,255,0.13)",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14)",
                    },
                    "&.Mui-focusVisible": { outline: `2px solid ${tokens.gold}`, outlineOffset: -2 },
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
      </Box>

      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <TabPanel value={safeTab} index="SUMMARY" >
          <BotGroupContainer
            bots={bots}
            isHomeView={forceHomeTab}
            onDoubleClick={onDoubleClickBot}
            onDoubleClickSummaryBack={onDoubleClickBack}
            onSelectBot={(botId) => selectTab(botId, true)}
            activeNetwork={activeNetwork}
          />
        </TabPanel>
        <TabPanel value={safeTab} index="BUYBACKS">
          <BuybacksView buybacksData={buybacksData} pendingSummary={pendingBuybacksSummary} />
        </TabPanel>
        <TabPanel value={safeTab} index="TIMELINE">
          <TimelineView bots={bots} />
        </TabPanel>
        {tabOptions
          .filter((option) => !STATIC_TABS[option])
          .map((option) => (
            <TabPanel key={option} value={safeTab} index={option}>
              {currentBot && currentBot.stats.botName === option && (
                <BotStatsDetailedView
                  stats={currentBot.stats}
                  movements={currentBot.movements}
                  history={currentBot.history}
                  onEditMovement={handleEditMovement}
                  onDeleteMovement={handleDeleteMovement}
                  mode="full"
                  fullPositions={currentBot.positions}
                  onDoubleClick={onDoubleClickBack}
                  socket={socket}
                />
              )}
            </TabPanel>
          ))}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default BotStatsContainer;
